// React and Router
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Third-party libraries
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";
import { diffWords } from "diff";
import Select from "react-select";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";

// Components
import Sidebar from "../shared/Sidebar.js";
import ConflictResolver from "../documentation/ConflictResolver.js";
import WalkthroughSidebar from "./WalkthroughSidebar.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";

// Firebase
import { auth } from "../../firebase.js";

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";
import "../shared/UserMentionBlot";
import UserMentionDropdown from "../shared/UserMentionDropdown";

// Styles
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";

import "../../css/Create.css";
import "../../css/EditDocument.css";
import "../../css/TaskReference.css";
import "../../css/DocumentationHomeComponent.css";
import "../../css/CreateWalkthrough.css";
import "../../css/EditWalkthrough.css";
import "../../css/QuillOverrides.css";
import "../../css/DocEditorMakeover.css";
import { notifyBlockedShareRecipients, readShareResponse } from "../../utils/sharePermissions";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);
const Inline = Quill.import("blots/inline");
class InlineCode extends Inline {
  static create() {
    let node = super.create();
    node.setAttribute("class", "inline-code");
    return node;
  }

  static formats(node) {
    return true;
  }
}
InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";
Quill.register(InlineCode);

function EditWalkthrough() {
  const { tutorialName, originalAuthorUid } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Authenticating user...');
  const navigate = useNavigate();
  const tutorialCoverMaxLength = 10000;
  const [newTutorialName, setNewTutorialName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [emails, setEmails] = useState([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const { enhancedRightClickEnabled } = useQuickLinks();
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tutorialCover, setTutorialCover] = useState("");

  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [userUid, setUserUid] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [hasPermission, setHasPermission] = useState(true);
  const [documentOrder, setDocumentOrder] = useState([]);
  const [documentVersion, setDocumentVersion] = useState(null);

  const [customLink, setCustomLink] = useState("");
  const [customLinkTitle, setCustomLinkTitle] = useState("");
  const [discordLink, setDiscordLink] = useState("");
  const [linkedInLink, setLinkedInLink] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [githubLink, setGithubLink] = useState("");
  const [twitterLink, setTwitterLink] = useState("");
  const [blueskyLink, setBlueskyLink] = useState("");
  const [isPasting, setIsPasting] = useState(false);
  const quillRef = useRef(null);
  const { taskRefProps, userMentionProps } = useTaskReference(quillRef, { mode: "documentation", docType: "tutorial", docName: tutorialName });

  const walkthroughNameMaxLength = 150;

  useEffect(() => {
    setLoadingMessage('Authenticating user...');
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserUid(user.uid);
        setUserEmail(user.email);
      } else {
        setUserUid(null);
        setUserEmail(null);
        setHasPermission(false);
        setLoading(false);
      }
    });
  });

  // Add this after the ReactQuill component in the return statement
  useEffect(() => {
    if (quillRef.current) {
      // Get the toolbar element
      const toolbar = quillRef.current.getEditor().getModule('toolbar');
      
      // Find the undo and redo buttons and replace their content with Font Awesome icons
      const undoButton = toolbar.container.querySelector('.ql-undo');
      const redoButton = toolbar.container.querySelector('.ql-redo');
      
      if (undoButton) {
        undoButton.innerHTML = '<i class="fas fa-undo"></i>';
        undoButton.title = 'Undo';
      }
      
      if (redoButton) {
        redoButton.innerHTML = '<i class="fas fa-redo"></i>';
        redoButton.title = 'Redo';
      }
    }
  }, [quillRef.current]);

  useEffect(() => {
    if (originalAuthorUid && userUid && userEmail) {
      setNewTutorialName(tutorialName);
      fetchTutorialContent();
      fetchDocumentDetails();
      fetchPermissionsForThisTutorial();
    }
  }, [tutorialName, originalAuthorUid, userUid, userEmail]);

  const fetchTutorialContent = async () => {
    setLoadingMessage("Fetching walkthrough content...")
    if (!userUid || !userEmail) {
      setLoading(false);
      return;
    }
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        console.error("User is not authenticated.");
        return;
      }
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/walkthrough/${tutorialName}/${originalAuthorUid}/`,
        {
          method: "GET",
          headers: {
            "requesting-user-uid": userUid,
            "requesting-user-email": userEmail,
            "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
          },
        }
      );
      const data = await response.json();
      setLoading(false);
      if (response.status === 403) {
        setHasPermission(false);
      } else if (response.status === 404) {
        setHasPermission(false);
      }
      if (originalAuthorUid !== userUid) {
        setHasPermission(false);
        return;
      }
      setDescription(data.description);
      setCompleted(data.completed);
      setIsPublic(data.is_public);
      setIsPasting(true); // 🔥 Pause saving
      setTimeout(() => {
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();

          // Clean up the HTML first (remove <temporary> tags)
          let cleanHtml = data.tutorial_cover;
          cleanHtml = cleanHtml.replace(
            /<temporary[^>]*>.*?<\/temporary>/g,
            ""
          );

          // Convert cleaned HTML to Delta
          const delta = editor.clipboard.convert({ html: cleanHtml });

          // Get current cursor position (optional but nice for UX)
          const range = editor.getSelection();

          // Update editor content using Delta
          editor.updateContents(delta, "user");

          // Set cursor position properly (otherwise cursor will jump randomly)
          if (range) {
            editor.setSelection(
              delta.length() - (range.length || 0),
              0,
              "silent"
            );
          }

          editor.scrollSelectionIntoView();

          // Save clean HTML if needed (optional)
          setTutorialCover(editor.root.innerHTML);
        }
        setIsPasting(false);
      }, 1000);

      setDocumentVersion(data.version);
      setYoutubeLink(data.youtube_channel);
      setCustomLink(data.custom_domain);
      setCustomLinkTitle(data.custom_domain_title);
      setDiscordLink(data.discord_server);
      setLinkedInLink(data.linkedin_link);
      setGithubLink(data.github_link);
      setTwitterLink(data.twitter_link);
      setBlueskyLink(data.bluesky_link);
      setDocumentOrder(data.document_order);
      fetchDocumentDetailsByOrder(data.document_order);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching walkthrough content: ", error);
    }
  };

  const fetchDocumentDetailsByOrder = async (documentOrder) => {
    setLoadingMessage("Fetching document details by order...");
    if (!documentOrder || documentOrder.length === 0) {
      setSelectedDocuments([]);
      return;
    }
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        console.error("User is not authenticated.");
        return;
      }
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/`,
        {
          method: "POST",
          headers: {
            "requesting-user-uid": userUid,
            "requesting-user-email": userEmail,
            "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Content-Type": "application/json",
            mode: "tutorial",
          },
          body: JSON.stringify({ document_ids: documentOrder }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        setSelectedDocuments(data);
      }
    } catch (error) {
      console.error("Error fetching document details by order: ", error);
    }
  };

  const fetchDocumentDetails = async () => {
    setLoadingMessage("Fetching document details...");
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        console.error("User is not authenticated.");
        return;
      }
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/page-details/author/${originalAuthorUid}/`,
        {
          method: "GET",
          headers: {
            "Authorization": `${token}`,
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        const processedDocuments = data.map((doc) => ({
          id: doc.id,
          name: doc.space || doc.page_name,
          type: doc.space ? "Space" : "Page",
        }));
        setAvailableDocuments(processedDocuments);
      }
    } catch (error) {
      console.error("Error in fetching document details: ", error);
    }
  };
  const handleSubmit = async () => {
    // Name validation checks
    if (!newTutorialName || newTutorialName.trim() === "") {
      alert("Please enter a name for your walkthrough.");
      return;
    }

    // Check that name only contains letters and numbers
    if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(newTutorialName)) {
      alert(
        "Walkthrough name must contain only letters, numbers, spaces, periods, dashes, and underscores."
      );
      return;
    }

    const textOnly = tutorialCover.replace(/<[^>]*>/g, "");
    if (textOnly.length > tutorialCoverMaxLength) {
      alert(
        `Please keep the character count below ${tutorialCoverMaxLength}. Current count: ${textOnly.length}`
      );
      return;
    }

    // Set loading to true immediately after validation passes
    setIsSubmitting(true);

    try {
      await updatePermissionTableEntries();

      // Extract mention UIDs from Quill content and include for notifications
      const mentionedUids = (() => {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(tutorialCover, 'text/html');
        const spans = parsed.querySelectorAll('[data-user-uid]');
        const uids = [...new Set([...spans].map(s => s.getAttribute('data-user-uid')).filter(Boolean))];
        return uids.filter(u => u !== userUid);
      })();

      const data = {
        submittedVersion: documentVersion,
        customLink: customLink,
        customLinkTitle: customLinkTitle,
        twitterLink: twitterLink,
        blueskyLink: blueskyLink,
        discordLink: discordLink,
        linkedInLink: linkedInLink,
        githubLink: githubLink,
        youtubeLink: youtubeLink,
        completed: completed,
        description: description,
        authorUid: originalAuthorUid,
        documentOrder: documentOrder,
        newTutorialName: newTutorialName,
        tutorialCover: tutorialCover,
        lastUpdatedBy: userUid,
        isPublic: isPublic,
        mentionedUids: mentionedUids,
      };

      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        throw new Error("User is not authenticated.");
      }
      
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/walkthrough/edit/${tutorialName}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "requesting-user-uid": userUid,
            "requesting-user-email": userEmail,
            "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
          },
          body: JSON.stringify(data),
        }
      );
      
      if (response.ok) {
        navigate(`/walkthroughs/${newTutorialName}/${originalAuthorUid}`);
      } else if (response.status === 409) {
        // Handle version conflict
        setIsSubmitting(false);
        alert("There is a version conflict. Another user has updated this walkthrough. Please refresh and try again.");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update walkthrough.");
      }
    } catch (error) {
      console.error("Error in updating walkthrough: ", error);
      alert(error.message || "An error occurred while updating the walkthrough.");
      setIsSubmitting(false); // Stop loading on error
    }
  };

  const loggedOutOptions = [
      {
        label: "Login",
        icon: <i className="fas fa-sign-in-alt"></i>,
        url: "/login",
        onClick: () => navigate("/login"),
      },
      {
        label: "Register",
        icon: <i className="fas fa-user-plus"></i>,
        url: "/register",
        onClick: () => navigate("/register"),
      },
      {
        label: "Try My Notebook",
        icon: <i className="fas fa-book"></i>,
        url: "/my-notebook",
        onClick: () => navigate("/my-notebook"),
      },
      {
        label: "Visit YouTube Channel",
        icon: <i className="fab fa-youtube"></i>,
        url: "https://www.youtube.com/@schoolofintology4062",
        onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Join Discord",
        icon: <i className="fab fa-discord"></i>,
        url: "https://discord.gg/DYkESHwgVA",
        onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
      },
    ];

const loggedInOptions = [
  {
    label: "Submit Edits",
    icon: <i className="fas fa-paper-plane"></i>, // Optional: add an icon
    onClick: handleSubmit, // This will trigger your submit logic
  },
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => window.open("/", "_blank", "noopener,noreferrer"),
      },
  {
    label: "Your Documents",
    onClick: () => window.open("/documentation", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Walkthroughs",
    onClick: () => window.open("/walkthroughs", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Notebook",
    onClick: () => window.open("/my-notebook", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Workspace",
    onClick: () => window.open("/workspaces", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Account Settings",
    onClick: () => window.open(`/account/${userUid}`, "_blank", "noopener,noreferrer"),
  },
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = userUid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

  const updatePermissionTableEntries = async () => {
    const data = {
      name: newTutorialName,
      documentationType: "Tutorial",
      userEmail: emails,
      isPublic: isPublic,
      documentUid: originalAuthorUid,
      documentUrl: `walkthroughs/${newTutorialName}/${originalAuthorUid}/`,
      description: description,
    };

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        throw new Error("User is not authenticated.");
      }
      const response = await fetch(
        "https://bluebirddocumentationadmin.pythonanywhere.com/permissions/edit/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "requesting-user-uid": userUid,
            "requesting-user-email": userEmail,
            "auth-token": `Bearer ${token}`,
            "Authorization": `${token}`,
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || "Failed to update permissions.");
      }

      // BBD-73: a 201 can still be partial — addresses on either side of a
      // block are skipped and reported back in blocked_emails.
      notifyBlockedShareRecipients(await readShareResponse(response));
      
    } catch (error) {
      console.error("Error updating permissions: ", error);
      throw error; // Propagate the error to be caught by the parent function
    }
  };

  const fetchPermissionsForThisTutorial = async () => {
    setLoadingMessage("Fetching permissions for this tutorial...");
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        console.error("User is not authenticated.");
        return;
      }
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/permissions/tutorial/${tutorialName}/`,
        {
          method: "GET",
          headers: {
            "requesting-user-uid": userUid,
            "requesting-user-email": userEmail,
            "auth-token": `Bearer ${token}`,
            "Authorization": `${token}`,
          },
        }
      );
      const data = await response.json();
      const existingPermissions = data.map((permission) => {
        let permissionLevel;
        if (permission.can_edit) {
          permissionLevel = "Can Edit";
        } else if (permission.can_view) {
          permissionLevel = "Can View";
        } else {
          permissionLevel = "";
        }

        return {
          email: permission.user_email,
          permission: permissionLevel,
        };
      });
      setEmails(existingPermissions);
    } catch (error) {
      console.error("Error fetching permission data: ", error);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [FONT_TOOLBAR_ENTRY],
          [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
          [{ size: ["small", false, "large"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["inline-code"],
          ["formula"],
          ["table-better"], // table button
          ["clean"],
          ["image"],
          ["undo", "redo"], // Add undo and redo buttons
        ],
        handlers: {
          undo: function() {
            this.quill.history.undo();
          },
          redo: function() {
            this.quill.history.redo();
          }
        }
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
      },
      table: false,
      "table-better": {
        language: "en_US",
        menus: [
          "column",
          "row",
          "merge",
          "table",
          "cell",
          "wrap",
          "copy",
          "delete",
        ],
        toolbarTable: true,
        tableCreationAttributes: {
          className: 'responsive-table',
          style: 'width: 100%; max-width: 100%;'
        }
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }),
    []
  );
  
  const handleAddEmail = () => {
    if (currentEmail && !emails.some((item) => item.email === currentEmail)) {
      setEmails([...emails, { email: currentEmail, permission: "Can View" }]);
      setCurrentEmail("");
    } else {
      alert("This email has already been added.");
    }
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = [...emails];
    updatedEmails.splice(index, 1);
    setEmails(updatedEmails);
  };

  const handlePermissionChange = (index, newPermission) => {
    const updatedEmails = [...emails];
    updatedEmails[index].permission = newPermission;
    setEmails(updatedEmails);
  };

  const handleAddDocument = (document) => {
    if (!selectedDocuments.some((doc) => doc.id === document.id)) {
      setSelectedDocuments([...selectedDocuments, document]);
      setDocumentOrder([...documentOrder, document.id]);
    } else {
      alert("Document already added.");
    }
  };

  const handleRemoveDocument = (index) => {
    const updatedDocuments = [...selectedDocuments];
    updatedDocuments.splice(index, 1);
    setSelectedDocuments(updatedDocuments);

    const updatedOrder = [...documentOrder];
    updatedOrder.splice(index, 1);
    setDocumentOrder(updatedOrder);
  };

  const handleReorderDocuments = (startIndex, endIndex) => {
    const result = Array.from(selectedDocuments);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    setSelectedDocuments(result);

    const orderResult = Array.from(documentOrder);
    const [removedOrder] = orderResult.splice(startIndex, 1);
    orderResult.splice(endIndex, 0, removedOrder);
    setDocumentOrder(orderResult);
  };

  const handleCompletedToggle = (e) => {
    setCompleted(e.target.checked);
  };
  const handleToggle = (e) => {
    setIsPublic(e.target.checked);
  };
  const handleHomeButtonClick = () => {
    navigate("/");
  };
  const handleLoginButtonClick = () => {
    navigate("/login");
  };
  const handleRegisterButtonClick = () => {
    navigate("/register");
  };

  if (loading) {
    return (
      <div className="DocumentationHomeComponent__coffee-loader-div">
        <div className="coffee-cup-loader">
          <div className="cup">
            <div className="cup-body"></div>
            <div className="cup-handle"></div>
            <div className="cup-base"></div>
            <div className="cup-shadow"></div>
            <div className="steam steam1"></div>
            <div className="steam steam2"></div>
            <div className="steam steam3"></div>
          </div>
        </div>
        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
      </div>
    );
  }

  // Add this right after the initial loading and permission checks
    if (isSubmitting) {
      return (
        <div className="DocumentationHomeComponent__coffee-loader-div">
          <div className="coffee-cup-loader">
            <div className="cup">
              <div className="cup-body"></div>
              <div className="cup-handle"></div>
              <div className="cup-base"></div>
              <div className="cup-shadow"></div>
              <div className="steam steam1"></div>
              <div className="steam steam2"></div>
              <div className="steam steam3"></div>
            </div>
          </div>
          <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>Submitting your changes...</p>
        </div>
      );
    }

  if (loading === false && hasPermission === false) {
    return (
      <div 
        className="DocumentationMainPage__div DocEditor"
      onContextMenu={handleContextMenu}
        >
        {/* <Sidebar /> */}
        <div className="DocumentationMainPage__main-content">
          <p className="large">
            It looks like you don't have permission to edit this walkthrough.
          </p>
          <p className="medium">
            <strong>You might just need to sign in</strong>. If this is your
            walkthrough, make sure you're signed in. If this issue persists,
            this could be a bug on our end. We are very sorry for the
            inconvenience; please let us know this has happened.
          </p>
          <p className="medium">
            <strong>If this is not your walkthrough</strong>, check with the
            original author of the walkthrough to see if you have correct
            permissions.
          </p>
          <button
            onClick={handleHomeButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Home
          </button>
          <button
            onClick={handleLoginButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Login
          </button>
          <button
            onClick={handleRegisterButtonClick}
            className="ConflictResolver__button"
          >
            Register
          </button>
        </div>
        {enhancedRightClickEnabled && ContextMenuComponent}
      </div>
    );
  }

  return (
    <div 
        className="Create__div DocEditor"
      onContextMenu={handleContextMenu}
    >
      <WalkthroughSidebar />

      <div className="Create__main-content">
        <h2>Edit Walkthrough</h2>

        <div className="Create__form-group">
          <label>Edit Name: </label>
          <input
            type="text"
            className="Create__input-short-field"
            value={newTutorialName}
            onChange={(e) => setNewTutorialName(e.target.value)}
            maxLength={walkthroughNameMaxLength}
            placeholder={`Max ${walkthroughNameMaxLength} characters`}
          />
        </div>

        <div className="Create__form-group">
          <label>Edit Description: </label>
          <input
            type="text"
            id="description"
            className="Create__input-full-field"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            This description is what invited users will see about this
            walkthrough. It will also appear on the cover page of the
            walkthrough itself.
          </p>
        </div>

        <div className="Create__form-group">
          <label>Edit Public Status: </label>
          <label className="Create__switch">
            <input type="checkbox" checked={isPublic} onChange={handleToggle} />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Public content is viewable by anyone that has this document's URL.
          </p>
        </div>

        <div className="Create__form-group">
          <label>Publish this walkthrough upon finishing changes?</label>
          <label className="Create__switch">
            <input
              type="checkbox"
              checked={completed}
              onChange={handleCompletedToggle}
            />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Whether the walkthrough is public or not, this walkthrough will not
            be <strong>viewable</strong> by anyone; including added
            collaborators that have "Can View" permission. Collaborators that
            have "Can Edit" permission can edit this walkthrough.
          </p>
        </div>

        <div className="Create__form-group CreateWalkthrough__document-order-div">
          <label>Select and Order Documents (Spaces/Pages):</label>
          <div className="Create__form-group-helper-text">
            <i
              className="fas fa-info-circle Create__info-icon"
              title="Information"
            ></i>
            <p className="Create__helper-text">
              To make your walkthrough, select which documents you'd like to
              include in the walkthrough. Then you can also adjust the order in
              which documents appear in the walkthrough as well below.
            </p>
          </div>

          {/* Add the Select component for document search here */}
          <Select
            options={availableDocuments.map((doc) => ({
              value: doc,
              label: `${doc.type}: ${doc.name}`,
            }))}
            onChange={(selected) => {
              if (selected) {
                handleAddDocument(selected.value);
              }
            }}
            placeholder="Search for documents..."
            isClearable
            className="CreateWalkthrough__document-search"
          />

          <div className="CreateWalkthrough__available-documents">
            {availableDocuments.map((doc) => (
              <button
                key={doc.id}
                onClick={() => handleAddDocument(doc)}
                className="CreateWalkthrough__add-document-button"
              >
                <strong>{doc.type}:</strong> {doc.name}
              </button>
            ))}
          </div>

          <div className="CreateWalkthrough__selected-documents">
            {selectedDocuments.map((doc, index) => (
              <div
                key={doc.id}
                className="CreateWalkthrough__selected-document-item"
              >
                <span>
                  <strong>{doc.type}:</strong> {doc.name}
                </span>
                <button
                  onClick={() => handleRemoveDocument(index)}
                  className="CreateWalkthrough__remove-document-button"
                >
                  Remove
                </button>
                {index > 0 && (
                  <button
                    onClick={() => handleReorderDocuments(index, index - 1)}
                    className="CreateWalkthrough__move-up-button"
                  >
                    Move Up
                  </button>
                )}
                {index < selectedDocuments.length - 1 && (
                  <button
                    onClick={() => handleReorderDocuments(index, index + 1)}
                    className="CreateWalkthrough__move-down-button"
                  >
                    Move Down
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div
          className="Create__form-group"
          style={{ flexDirection: "column", marginBottom: "15px" }}
        >
          <label>Invite others to collaborate on this content.</label>
          {emails.map((item, index) => (
            <div
              key={index}
              className="Create__email-item"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <span>{item.email}</span>
              <select
                value={item.permission}
                className="Create__select"
                onChange={(e) => handlePermissionChange(index, e.target.value)}
                style={{ marginLeft: "10px", padding: "5px" }}
              >
                {/* <option value="Can Edit">Can Edit</option> */}
                <option value="Can View">Can View</option>
              </select>
              <button
                onClick={() => handleRemoveEmail(index)}
                style={{
                  marginLeft: "10px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <i
                  className="fas fa-trash-alt"
                  title="Remove Email"
                  style={{ color: "white" }}
                ></i>
              </button>
            </div>
          ))}
          <div className="Create__email-input-container">
            <input
              type="email"
              className="Create__email-input"
              placeholder="Enter E-mail Address"
              value={currentEmail}
              onChange={(e) => setCurrentEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
            />
            <button onClick={handleAddEmail} className="Create__add-email-btn">
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            <strong>Please note</strong>, as of this current release,{" "}
            <strong>
              any documents you attach to this walkthrough will be visible to
              whomever you share this walkthrough with
            </strong>
            ; regardless of the individual document permissions. Please be
            mindful of this before sharing any documentation that might be
            sensitive.
          </p>
        </div>
        <div
          className="Create__form-group-helper-text"
          style={{ marginBottom: "60px" }}
        >
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            As of the current release, you cannot add editors to walkthroughs.
            This will be added once we add other permission roles like admin and
            such.
          </p>
        </div>

        <input
          type="text"
          placeholder="Custom Link Title"
          value={customLinkTitle}
          onChange={(e) => setCustomLinkTitle(e.target.value)}
          className="Account__input-custom-title"
        />
        <input
          id="customLinkInput"
          type="text"
          placeholder="Custom Link Domain"
          value={customLink}
          onChange={(e) => setCustomLink(e.target.value)}
          className="Account__edit-input"
        />
        <div className="Account__link-divs">
          <i class="fa-brands fa-discord Account__icons"></i>
          <input
            type="text"
            value={discordLink}
            placeholder="Discord Server Link"
            onChange={(e) => setDiscordLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>
        <div className="Account__link-divs">
          <i class="fa-brands fa-square-x-twitter Account__icons"></i>
          <input
            type="text"
            value={twitterLink}
            placeholder="X Profile Link"
            onChange={(e) => setTwitterLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>
        <div className="Account__link-divs">
          <i class="fa-brands fa-bluesky Account__icons"></i>
          <input
            type="text"
            value={blueskyLink}
            placeholder="Bluesky Profile Link"
            onChange={(e) => setBlueskyLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>
        <div className="Account__link-divs">
          <i class="fa-brands fa-linkedin Account__icons"></i>
          <input
            type="text"
            value={linkedInLink}
            placeholder="LinkedIn Profile Link"
            onChange={(e) => setLinkedInLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>
        <div className="Account__link-divs">
          <i class="fa-brands fa-youtube Account__icons"></i>
          <input
            type="text"
            value={youtubeLink}
            placeholder="YouTube Channel Link"
            onChange={(e) => setYoutubeLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>
        <div className="Account__link-divs">
          <i class="fa-brands fa-square-github Account__icons"></i>
          <input
            type="text"
            value={githubLink}
            placeholder="GitHub Profile Link"
            onChange={(e) => setGithubLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>
        <div
          className="Create__form-group-helper-text"
          style={{ marginBottom: "60px" }}
        >
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            These are links that users will see when visiting this walkthrough.
            The links are automatically pulled from what's saved in your account
            settings. Feel free to edit any of the links if you don't want to
            use what's pulled from your account settings.{" "}
            <strong>
              If you don't want your saved link(s) to show for this particular
              walkthrough,{" "}
            </strong>
            just remove the contents of that input; until you see the
            placeholder text again.
          </p>
        </div>

        <div className="EditDocument__quill-container">
          <div className="QuillOverrides__content-formatting">
          <ReactQuill
            ref={quillRef}
            value={tutorialCover}
            onChange={(content) => {
              if (isPasting) return;
              const textOnly = content.replace(/<[^>]*>/g, "");
              if (textOnly.length <= tutorialCoverMaxLength) {
                setTutorialCover(content);
              } else {
                alert(
                  `Please keep the character count below ${tutorialCoverMaxLength}. Current count: ${textOnly.length}`
                );
              }
            }}
            modules={modules}
            style={{ height: "70vh" }}
          />
          <TaskReferenceDropdown {...taskRefProps} />
          <UserMentionDropdown {...userMentionProps} />
          </div>
        </div>
        <div className="Create__form-group-helper-text CreateWalkthrough__tutorial-cover-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Characters: {tutorialCover.replace(/<[^>]*>/g, "").length} /{" "}
            {tutorialCoverMaxLength}
          </p>
        </div>
        <button onClick={handleSubmit} className="submit-button">
          Submit
        </button>
      </div>
      {enhancedRightClickEnabled && ContextMenuComponent}
    </div>
  );
}

export default EditWalkthrough;
